import { Button } from "@/core/components/ui/button";
import { Input } from "@/core/components/ui/input";
import { Label } from "@/core/components/ui/label";
import { Pencil } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { fromApiPhone } from "../../mappers/phone";

interface PhoneFieldProps {
  value: string;
  onChange: (value: string) => void;
  errorMessage?: string;
}

/**
 * Um único campo que alterna entre leitura e edição: em repouso mostra o
 * telefone com o botão "Atualizar"; ao clicar, o mesmo campo fica editável e o
 * botão vira "Salvar". Enquanto não se confirma, o valor do form não muda.
 */
export function PhoneField({ value, onChange, errorMessage }: PhoneFieldProps) {
  const [translate] = useTranslation(["insights"]);
  const [isEditing, setIsEditing] = useState(false);
  const [draft, setDraft] = useState("");

  const startEditing = () => {
    // Parte do valor formatado: ao editar um agendamento salvo, `value` chega
    // como dígitos crus da API (5519999999999).
    setDraft(fromApiPhone(value));
    setIsEditing(true);
  };

  const save = () => {
    onChange(draft.trim());
    setIsEditing(false);
  };

  return (
    <div className="flex flex-col gap-1.5">
      <Label>{translate("schedule-field-phone")}</Label>
      <div className="flex gap-3 items-center">
        {isEditing ? (
          <>
            <Input
              autoFocus
              value={draft}
              onChange={(event) => setDraft(event.target.value)}
              placeholder="(19) 9 9999-9999"
              className="flex-1"
              // Enter salva sem submeter o form inteiro.
              onKeyDown={(event) => {
                if (event.key === "Enter") {
                  event.preventDefault();
                  save();
                }
              }}
            />
            <Button
              type="button"
              variant="outline"
              size="lg"
              className="shrink-0"
              onClick={save}
            >
              {translate("schedule-phone-save")}
            </Button>
          </>
        ) : (
          <>
            <Input
              value={fromApiPhone(value)}
              readOnly
              disabled
              className="flex-1"
            />
            <button
              type="button"
              onClick={startEditing}
              className="flex gap-1.5 items-center text-sm font-bold shrink-0 text-neutral-900 hover:underline"
            >
              <Pencil className="w-4 h-4" />
              {translate("schedule-phone-update")}
            </button>
          </>
        )}
      </div>

      {errorMessage && <p className="text-xs text-red-500">{errorMessage}</p>}
    </div>
  );
}
